import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Shield, Users, BarChart3, Briefcase, Lock, Mail, Linkedin } from 'lucide-react';
import { ENTITY } from '../../lib/entity';
import { InstitutionalInfrastructure } from './InstitutionalInfrastructure';

export const About = () => (
  <div data-testid="about-page-content">
    {/* Positioning */}
    <section className="py-20" style={{ background: 'var(--gpc-paper)' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-16">
          <div className="lg:col-span-3">
            <p className="font-inter text-sm text-navy uppercase tracking-wider mb-4">Who We Are</p>
            <h1 className="font-grotesk text-3xl font-semibold text-foreground mb-6 leading-snug">
              One GP Capital. Two finance pathways.
            </h1>
            <div className="space-y-4 font-inter text-muted-foreground leading-relaxed">
              <p>
                GP Capital is an Australian finance business operating across Private Capital and a dedicated Home Loans pathway in preparation.
              </p>
              <p>
                <strong>Home Loans.</strong> A planned residential mortgage pathway for individuals and property investors, supported by finance calculators available today. Credit assistance and home-loan enquiries will open only after authorisation and consumer disclosures are confirmed. <Link to="/home-loans" className="underline">Explore Home Loans</Link>.
              </p>
              <p>
                <strong>Private Capital.</strong> Capital solutions for complex property, corporate and structured transactions. Our controlled, confidential process connects borrowers and sponsors with private lenders, family offices and institutional investors. <Link to="/capital-solutions" className="underline">Explore Capital Solutions</Link>.
              </p>
              <p>
                Structured preparation, clear communication, technology-supported administration and human judgment connect the two divisions. Their services and regulatory processes remain separate.
              </p>
            </div>
          </div>
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-xl p-8" style={{ background: 'var(--gpc-panel)', border: '1px solid var(--gpc-line)' }}>
              <h3 className="font-grotesk text-lg font-semibold mb-5" style={{ color: 'var(--gpc-ink)' }}>Private Capital — How We Operate</h3>
              <ul className="space-y-4">
                {[
                  { icon: Shield, text: 'NDA-gated access to private-capital transactions' },
                  { icon: Users, text: 'Curated counterparty matching by mandate' },
                  { icon: BarChart3, text: 'Structured bid collection and comparison' },
                  { icon: Lock, text: 'Admin-controlled deal visibility' },
                  { icon: Briefcase, text: 'Full capital stack coverage' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(140,107,46,0.10)', border: '1px solid rgba(140,107,46,0.18)' }}>
                      <item.icon className="w-4 h-4" style={{ color: 'var(--gpc-gold)' }} />
                    </div>
                    <span className="font-inter text-sm" style={{ color: 'var(--gpc-ink-soft)' }}>{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Cross-border reach */}
    <section className="py-16" style={{ background: 'var(--gpc-panel)' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-inter text-sm text-navy uppercase tracking-wider mb-3">Cross-Border Reach</p>
            <h3 className="font-grotesk text-2xl font-semibold text-foreground mb-4">
              Institutional relationships across key markets
            </h3>
            <p className="font-inter text-muted-foreground leading-relaxed">
              Our counterparty network spans multiple jurisdictions. We facilitate transactions where capital requirements cross borders and where local knowledge of lender appetite, regulatory environment and market conditions is essential to execution.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {['Australia', 'Asia', 'Europe', 'USA'].map((geo) => (
              <div key={geo} className="bg-background rounded-xl p-6 border text-center">
                <Globe className="w-5 h-5 text-teal mx-auto mb-2" />
                <span className="font-grotesk font-semibold text-foreground">{geo}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Meet the Team */}
    <section className="py-16" style={{ background: 'var(--gpc-paper)' }} data-testid="meet-the-team">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <p className="font-inter text-sm text-navy uppercase tracking-wider mb-3">Meet the Team</p>
        <h3 className="font-grotesk text-2xl font-semibold text-foreground mb-3">
          The person behind GP Capital
        </h3>
        <p className="font-inter text-muted-foreground mb-10 max-w-2xl">
          Founder-led, with deep experience across capital markets, real estate finance, structured lending and institutional distribution. GP Capital is operated with a principal mindset — aligned with counterparties and focused on execution.
        </p>
        <div className="max-w-sm mx-auto">
          {[
            {
              photo: '/giorgio-primiceri.webp',
              initials: 'GP',
              name: 'Giorgio Primiceri',
              title: 'Founder & Managing Director',
              desc: 'Leads firm strategy, origination and counterparty relationships. Background in institutional real estate finance and cross-border capital markets.',
              email: ENTITY.contact.giorgio,
              mailSubject: ENTITY.mailSubjects.giorgio,
              linkedin: ENTITY.social.giorgioLinkedIn,
            },
          ].map((member) => (
            <div
              key={member.initials}
              className="bg-background rounded-xl p-6 border hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200"
              data-testid={`team-card-${member.initials.toLowerCase()}`}
            >
              {member.photo ? (
                <img width="80" height="80" loading="lazy" decoding="async" src={member.photo} alt={member.name} className="w-20 h-20 rounded-full object-cover object-top mb-4" />
              ) : (
                <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4" style={{ background: 'var(--gpc-navy)' }}>
                  <span className="font-grotesk text-xl font-bold text-white">{member.initials}</span>
                </div>
              )}
              <h4 className="font-grotesk font-semibold text-foreground text-lg">{member.name}</h4>
              <p className="font-inter text-sm text-[var(--gpc-ink-soft)] font-medium mt-0.5">{member.title}</p>
              <p className="font-inter text-sm text-muted-foreground mt-3 leading-relaxed">{member.desc}</p>
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border">
                <a
                  href={`mailto:${member.email}?subject=${encodeURIComponent(member.mailSubject)}`}
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-teal transition-colors"
                  data-testid={`team-email-${member.initials.toLowerCase()}`}
                >
                  <Mail className="w-3.5 h-3.5" />
                  {member.email}
                </a>
                <a
                  href={member.linkedin}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-teal transition-colors"
                  data-testid={`team-linkedin-${member.initials.toLowerCase()}`}
                >
                  <Linkedin className="w-3.5 h-3.5" />
                  LinkedIn
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    {/* Institutional Infrastructure — factual credibility layer */}
    <InstitutionalInfrastructure />

    <section className="py-16" style={{ background: 'var(--gpc-panel)', borderTop: '1px solid var(--gpc-line-soft)' }}>
      <div className="max-w-3xl mx-auto px-6 text-center">
        <p className="gpc-eyebrow" style={{ marginBottom: '12px' }}>Work with us</p>
        <h3
          style={{
            fontFamily: 'Fraunces, "Playfair Display", Georgia, serif',
            fontWeight: 400,
            fontSize: 'clamp(1.75rem, 3vw, 2.25rem)',
            color: 'var(--gpc-ink)',
            marginBottom: '14px',
          }}
        >
          Work with GP Capital.
        </h3>
        <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '1rem', color: 'var(--gpc-ink-soft)', marginBottom: '28px' }}>
          Whether you are seeking capital or looking to deploy it, our platform provides a structured, institutional pathway to execution.
        </p>
        <div className="flex justify-center gap-3 flex-wrap">
          <Link to="/contact" className="gpc-cta-primary" data-testid="about-cta-submit">
            Submit a Deal <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/portal/login" className="gpc-cta-secondary" data-testid="about-cta-investor">
            Investor Access
          </Link>
        </div>
      </div>
    </section>
  </div>
);
